import copy from 'copy-to-clipboard';
import dayjs, { extend, UnitType } from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import * as XLSX from 'xlsx-js-style';

import { GroupedOption, Option, Value } from '@components/FormInput/types';
import { AgentData } from '@customTypes/api/Agent';
import {
  GetResponseData as ArePerformanceResponseData,
  GetResponseDataV2 as ArePerformanceResponseDataV2,
} from '@customTypes/api/AREPerformance';
import { GetResponseData as AreTargetResponseData } from '@customTypes/api/ARETarget';
import { GetResponseData as JoinVisitSummary } from '@customTypes/api/JoinVisitSummary';
import AREDetailWithPerformance from '@customTypes/AREDetailWithPerformance';
import {
  EARTH_RADIUS,
  LOCALE_CONSTANTS,
  SET_TARGET_TIME,
} from '@src/constants';
import { showInfo, showSuccess } from '@src/utils/Toastify';

extend(isBetween);
extend(timezone);
extend(utc);

const { LOCALE, LOCALE_CURRENCY } = LOCALE_CONSTANTS;
const formatNumber = (
  num?: number | string | null,
  withCurrency?: boolean,
  locale: string = LOCALE,
  currency: string = LOCALE_CURRENCY,
) => {
  const currencyNumber = parseInt(`${num}`, 10);
  if (isNaN(currencyNumber)) {
    return '-';
  }
  return new Intl.NumberFormat(locale, {
    ...(withCurrency ? { style: 'currency' } : {}),
    currency,
    minimumFractionDigits: 0,
  }).format(currencyNumber);
};

const handleNumberCalculation = (value?: number): number => {
  let returnValue = 0;
  if (value && Number(value)) returnValue = value;

  if ([NaN, Infinity, undefined].includes(value)) returnValue = 0;

  return returnValue;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getPropByString = (obj: any, propString: string) => {
  if (!propString) return obj;
  const props = propString.split('.');
  let i = 0;
  for (let iLen = props.length - 1; i < iLen; i++) {
    const prop = props[i];

    const value = obj[prop];
    if (value !== undefined) {
      obj = value;
    }
  }
  return obj[props[i]];
};

export enum SortDirections {
  Ascending = 'asc',
  Descending = 'desc',
  NoDirection = '',
}

const sortList = <TData>(
  data: TData[],
  sortOrderField: string,
  sortDirection: SortDirections = SortDirections.Ascending,
): TData[] => {
  if (!data?.length) return [];
  if (sortDirection === SortDirections.NoDirection) return data;

  const newList = [...data];
  const firstObject = newList[0];
  const fieldValue = getPropByString(firstObject, sortOrderField);
  const isNumber: boolean = typeof fieldValue === 'number';

  const isAscendingSort: boolean = sortDirection === SortDirections.Ascending;

  if (isNumber) {
    newList.sort((a, b) => {
      const aValue = getPropByString(a, sortOrderField);
      const bValue = getPropByString(b, sortOrderField);
      const ascendingSortNumber = aValue - bValue;
      const descendingSortNumber = bValue - aValue;
      return isAscendingSort ? ascendingSortNumber : descendingSortNumber;
    });
  } else {
    newList.sort((a, b) => {
      const aValue = String(parseValue(getPropByString(a, sortOrderField), ''));
      const bValue = String(parseValue(getPropByString(b, sortOrderField), ''));

      return isAscendingSort
        ? aValue.localeCompare(bValue, undefined, { sensitivity: 'base' })
        : bValue.localeCompare(aValue, undefined, { sensitivity: 'base' });
    });
  }

  return newList;
};

export type FieldDirectionMap = Record<string, SortDirections>;
const sortListMultipleField = <TData>(
  data: TData[],
  fieldDirectionMap: FieldDirectionMap,
): TData[] => {
  const newList = [...data];
  const sortFields = Object.keys(fieldDirectionMap);
  const sortDirections = Object.values(fieldDirectionMap);

  newList.sort((a, b) => {
    for (let i = 0; i < sortFields.length; i++) {
      const sortOrderField = sortFields[i];
      const sortDirection = sortDirections[i];
      const aValue = getPropByString(a, sortOrderField);
      const bValue = getPropByString(b, sortOrderField);
      const compareResult =
        typeof aValue === 'string'
          ? aValue.localeCompare(bValue)
          : aValue - bValue;
      if (compareResult !== 0) {
        return sortDirection === SortDirections.Ascending
          ? compareResult
          : -compareResult;
      }
    }
    return 0;
  });

  return newList;
};

const onlyUnique = <T>(value: T, index: number, self: T[]) => {
  return self.indexOf(value) === index;
};

const distinctArray = <TArray>(array: TArray[]): TArray[] => {
  let uniqueArray: TArray[] = [];
  array?.length && (uniqueArray = array.filter(onlyUnique));
  return uniqueArray;
};

const getCurrentDateOfMonth = () =>
  Number((import.meta.env.VITE_SET_GLOBAL_DATE ?? '') || dayjs().date());

const getCurrentMonth = () =>
  Number((import.meta.env.VITE_SET_GLOBAL_MONTH ?? '') || dayjs().format('M'));

const getCurrentYear = () =>
  Number(
    (import.meta.env.VITE_SET_GLOBAL_YEAR ?? '') || dayjs().format('YYYY'),
  );

const getCurrentDate = () =>
  dayjs()
    .year(getCurrentYear())
    .month(getCurrentMonth() - 1)
    .date(getCurrentDateOfMonth());

const filteredPhoneNumber = (phoneNumber: string, withFormat = false) => {
  if (!phoneNumber || phoneNumber?.length < 7) {
    return phoneNumber;
  }
  let newPhoneNumber = phoneNumber.replaceAll(/\D/g, '');
  if (newPhoneNumber?.startsWith('62')) {
    newPhoneNumber = newPhoneNumber?.substring(2);
  } else if (newPhoneNumber?.startsWith('0')) {
    newPhoneNumber = newPhoneNumber?.substring(1);
  }
  if (withFormat) {
    const newTraillingNumber = newPhoneNumber
      .substring(3)
      .match(/.{1,4}/g) as RegExpMatchArray; // casted to it is impossible for this match to be null
    newPhoneNumber = `+62 ${[
      newPhoneNumber.substring(0, 3),
      ...newTraillingNumber,
    ].join('-')}`;
  }
  return newPhoneNumber;
};

const getWeekRangeList = ({
  count = 1,
  startDateOffset = 0,
  endDateOffset = 0,
  sort = SortDirections.Ascending,
}) => {
  type WeekRange = [Date, Date];
  const weekOffsetList = Array.from(Array(count).keys());

  if (sort === SortDirections.Descending) {
    weekOffsetList.sort((a, b) => b - a);
  }

  return weekOffsetList.map(
    weekOffset =>
      getWeekRangeFromDateForCurrentMonth({
        date: dayjs()
          .month(getCurrentMonth() - 1)
          .add(weekOffset, 'weeks')
          .toDate(),
        startDateOffset,
        endDateOffset,
      }) as WeekRange,
  );
};

const getWeekRangeFromDateForCurrentMonth = ({
  date,
  currentMonthOnly = false,
  startDateOffset = 0,
  endDateOffset = 0,
}: {
  date?: Date | null;
  currentMonthOnly?: boolean;
  startDateOffset?: number;
  endDateOffset?: number;
} = {}): [Date, Date] => {
  const startMonthDate = getCurrentDate().startOf('month').toDate();
  const endMonthDate = getCurrentDate().endOf('month').toDate();
  let weekStartDate = dayjs(date)
    .startOf('week')
    .add(startDateOffset, 'days')
    .toDate();
  let weekEndDate = dayjs(date)
    .endOf('week')
    .add(endDateOffset, 'days')
    .toDate();

  if (!currentMonthOnly) return [weekStartDate, weekEndDate];

  if (dayjs(weekStartDate).isBefore(startMonthDate)) {
    weekStartDate = startMonthDate;
  }
  if (dayjs(weekEndDate).isAfter(endMonthDate)) {
    weekEndDate = endMonthDate;
  }
  return [weekStartDate, weekEndDate];
};

export const convertLocalTimeToUTC = (
  date: Date,
  props?: { [key in UnitType]?: number },
) => {
  let newDate = dayjs(date);

  if (props) {
    Object.entries(props).forEach(([unit, value]) => {
      newDate = newDate.set(unit as UnitType, value);
    });
  }

  return `${newDate.format('YYYY-MM-DDTHH:mm:ss')}Z`;
};

export const getMonthRangeList = (count = 3) =>
  Array.from(Array(count).keys()).map(offset => {
    const date = dayjs().subtract(offset, 'month');
    return {
      label: date.format('MMMM YYYY'),
      value: [date.startOf('month').toDate(), date.endOf('month').toDate()] as [
        Date,
        Date,
      ],
    };
  });

const getAgentRankingName = (rankNumber?: string) => {
  switch (rankNumber) {
    case '4':
      return 'Platinum';
    case '3':
      return 'Gold';
    case '2':
      return 'Silver';
    case '1':
    default:
      return 'Bronze';
  }
};

const getAgentRankingNumber = (rankName?: string) => {
  switch (rankName) {
    case 'Platinum':
      return '4';
    case 'Gold':
      return '3';
    case 'Silver':
      return '2';
    case 'Bronze':
    default:
      return '1';
  }
};

const getMergedArray = <TData>(
  arrayList: (Array<TData> | undefined)[],
): TData[] => {
  let result: TData[] = [];

  arrayList.forEach(data => {
    if (!data) return;
    result = result.concat(data);
  });

  return result;
};

const isDateInSetTargetPeriod = (date: Date) => {
  return dayjs(date).isBetween(
    dayjs()
      .year(getCurrentYear())
      .month(getCurrentMonth() - 1)
      .date(SET_TARGET_TIME.START_DATE)
      .hour(SET_TARGET_TIME.START_HOUR)
      .startOf('hour'),
    dayjs()
      .year(getCurrentYear())
      .month(getCurrentMonth() - 1)
      .date(SET_TARGET_TIME.END_DATE)
      .hour(SET_TARGET_TIME.END_HOUR)
      .startOf('hour'),
  );
};

const getAREDetailWithPerformance = (
  areMarketingId: string,
  areList?: AgentData[],
  arePerformanceDetail?: ArePerformanceResponseData[],
): AREDetailWithPerformance => {
  const updatedAreResponseData =
    areList &&
    (areList.find(
      ({ agent_confins: { marketing_id } }) => marketing_id === areMarketingId,
    ) ??
      areList[0]);

  const performanceDetails = arePerformanceDetail?.find(
    ({ are_marketing_id }) =>
      are_marketing_id === updatedAreResponseData?.agent_confins.marketing_id,
  );

  //update specificAreDetails data on dropdownAREList value change (from are detail & performance)
  return {
    are_marketing_id: updatedAreResponseData?.agent_confins.marketing_id,
    are_name: parseValue(
      updatedAreResponseData?.agent_confins.emp_name,
      updatedAreResponseData?.agent.agent_name,
    ),
    are_email: updatedAreResponseData?.agent_confins.corporate_email,
    are_phone_number: updatedAreResponseData?.agent_confins.mobile_phone_no,
    ba_active: performanceDetails?.performance?.ba_active,
    ba_prospect: performanceDetails?.performance?.ba_prospect,
    ba_visit: Number(performanceDetails?.performance?.ba_visit) || 0,
    bap: performanceDetails?.performance?.bap,
    ntf_achieved: performanceDetails?.performance?.ntf_achieved,
    total_contract: performanceDetails?.performance?.total_contract,
    ntf_target: performanceDetails?.performance?.ntf_target,
    total_contract_target:
      performanceDetails?.performance?.total_contract_target,
    new_register_agent: performanceDetails?.performance?.new_register_agent,
  };
};

const getAREDetailWithPerformanceV2 = (
  areMarketingId: string,
  areList?: AgentData[],
  arePerformanceDetail?: ArePerformanceResponseDataV2[],
): AREDetailWithPerformance => {
  const updatedAreResponseData =
    areList &&
    (areList.find(
      ({ agent_confins: { marketing_id } }) => marketing_id === areMarketingId,
    ) ??
      areList[0]);
  const performanceDetails = arePerformanceDetail?.find(
    ({ are_marketing_id }) =>
      are_marketing_id === updatedAreResponseData?.agent_confins.marketing_id,
  );

  //update specificAreDetails data on dropdownAREList value change (from are detail & performance)
  return {
    are_marketing_id: updatedAreResponseData?.agent_confins.marketing_id,
    are_name: parseValue(
      updatedAreResponseData?.agent_confins.emp_name,
      updatedAreResponseData?.agent.agent_name,
    ),
    are_email: updatedAreResponseData?.agent_confins.corporate_email,
    are_phone_number: updatedAreResponseData?.agent_confins.mobile_phone_no,
    product_id: updatedAreResponseData?.product_id,
    ba_active: performanceDetails?.active_agent ?? undefined,
    ba_prospect: performanceDetails?.prospect_agent ?? undefined,
    ba_visit:
      performanceDetails?.maintain_agent && performanceDetails?.active_agent
        ? Math.round(
            (performanceDetails?.maintain_agent /
              performanceDetails?.active_agent) *
              100,
          )
        : undefined,
    bap: performanceDetails?.productive_agent ?? undefined,
    ntf_achieved: performanceDetails?.ntf_total
      ? Number(performanceDetails?.ntf_total)
      : undefined,
    total_contract: performanceDetails?.contract_total ?? undefined,
    ntf_target: performanceDetails?.target?.ntf_target
      ? Number(performanceDetails?.target?.ntf_target)
      : undefined,
    total_contract_target:
      performanceDetails?.target?.total_contract ?? undefined,
    new_register_agent: performanceDetails?.new_agent ?? undefined,
  };
};

const doNothing = <TPayload = unknown>(_payload?: TPayload) => {
  /* do nothing */
};

const getJoinVisitSummaryAttributeListsByActivityType = (
  joinVisitSummary: JoinVisitSummary,
) =>
  joinVisitSummary.report_summary.reduce<
    Record<string, Record<string, number> | undefined>
  >(
    (previousSummary, { activity_type, categories }) => ({
      ...previousSummary,
      [activity_type?.toLowerCase()]: categories?.reduce(
        (previousCategories, { category, average }) => ({
          ...previousCategories,
          [category.toLowerCase()]: average,
        }),
        {},
      ),
    }),
    {},
  );

const getAchievementRateInPercentage = (
  target?: number | null,
  actual?: number | null,
) => {
  if (actual === undefined || actual === null) return '-';
  if (!target) return '0%';
  return `${Math.round((actual / target) * 100)}%`;
};

const getAreTargetDetailData = (areTarget?: AreTargetResponseData) =>
  areTarget
    ? {
        main_target: {
          ntf_target: areTarget.main_target.ntf_target,
          bap: areTarget.main_target.bap,
          active_agent: areTarget.main_target.active_agent,
          prospect_agent: areTarget.main_target.prospect_agent,
        },
        derived_target: {
          total_contract: areTarget.derived_target.total_contract,
          total_prospect: areTarget.derived_target.total_prospect,
          total_registered_agent:
            areTarget.derived_target.total_registered_agent,
        },
      }
    : {
        main_target: {
          ntf_target: 0,
          bap: 0,
          active_agent: 0,
          prospect_agent: 0,
        },
        derived_target: {
          total_contract: 0,
          total_prospect: 0,
          total_registered_agent: 0,
        },
      };

const getPerformanceAreDetailData = (
  arePerformance?: ArePerformanceResponseData,
) =>
  arePerformance
    ? {
        performance: {
          ntf_achieved: arePerformance.performance?.ntf_achieved,
          bap: arePerformance.performance?.bap,
          ba_prospect: arePerformance.performance?.ba_prospect,
          ba_active: arePerformance.performance?.ba_active,
          total_contract: arePerformance.performance?.total_contract,
          total_prospect: arePerformance.performance?.total_prospect,
          new_register_agent: arePerformance.performance?.new_register_agent,
        },
      }
    : {
        performance: {
          ntf_achieved: 0,
          bap: 0,
          ba_prospect: 0,
          ba_active: 0,
          total_contract: 0,
          total_prospect: 0,
          new_register_agent: 0,
        },
      };

const getPerformanceAreDetailDataV2 = (
  arePerformance?: ArePerformanceResponseDataV2,
) =>
  arePerformance
    ? {
        performance: {
          ntf_achieved:
            typeof arePerformance?.ntf_total === 'string'
              ? Number(arePerformance?.ntf_total)
              : arePerformance?.ntf_total,
          bap: arePerformance?.productive_agent,
          ba_prospect: arePerformance?.prospect_agent,
          ba_active: arePerformance?.active_agent,
          total_contract: arePerformance?.contract_total,
          total_prospect: arePerformance?.prospect_total,
          new_register_agent: arePerformance?.new_agent,
        },
      }
    : {
        performance: {
          ntf_achieved: 0,
          bap: 0,
          ba_prospect: 0,
          ba_active: 0,
          total_contract: 0,
          total_prospect: 0,
          new_register_agent: 0,
        },
      };

export const convertArrayOfJsonToAoa = (data: Record<string, string>[]) => {
  if (!data.length) return [];

  // Extract headers from the first object in the array
  const headers = Object.keys(data[0]);

  // Map each JSON object to an array of values
  const dataArray = data.map(obj => headers.map(key => obj[key]));

  return [headers, ...dataArray];
};

interface CellRange {
  s: { r: number; c: number };
  e: { r: number; c: number };
}
interface ApplyCellStyling {
  ws: XLSX.WorkSheet;
  style: XLSX.CellStyle;
  range: CellRange;
}
const applyCellStyling = ({ ws, style, range }: ApplyCellStyling) => {
  for (let colIndex = range.s.c; colIndex <= range.e.c; ++colIndex) {
    for (let rowIndex = range.s.r; rowIndex <= range.e.r; ++rowIndex) {
      const addr = XLSX.utils.encode_cell({ r: rowIndex, c: colIndex });
      if (!ws[addr]) continue;
      ws[addr].s = style;
    }
  }
};
interface ConvertToXlsxFileThenDownload {
  fileName?: string;
  data?: Record<string, string>[];
  customWidth?: Record<number, number>; // Record<colIndex, targetWidth>
  prefixRow?: string[];
  titleRow?: string;
}

const convertToXlsxFileThenDownload = ({
  fileName = dayjs().format('YYYYMMDD'),
  data = [],
  customWidth = {},
  prefixRow = [],
  titleRow,
}: ConvertToXlsxFileThenDownload) => {
  if (!data.length) return showInfo('No records available at the moment');

  const FILE_EXTENSION = '.xlsx';
  const DEFAULT_WIDTH = 20;
  const HEADER_BORDER = {
    style: 'thin' as XLSX.BorderType,
    color: { rgb: '2e2e2e' },
  };

  const REGULAR_BORDER = {
    style: 'thin' as XLSX.BorderType,
    color: { rgb: '818181' },
  };

  const addOnRow = titleRow ? [titleRow, ...prefixRow] : prefixRow;
  const addOnRowCount = addOnRow.length;
  const aoaOfData = [
    ...addOnRow.map(addOn => [addOn]),
    ...convertArrayOfJsonToAoa(data),
  ];

  const ws = XLSX.utils.aoa_to_sheet(aoaOfData);

  const sheetRange = XLSX.utils.decode_range(ws['!ref'] ?? '');

  // merge per add-on row
  ws['!merges'] = addOnRow.map((_, row) => ({
    s: { r: row, c: sheetRange.s.c },
    e: { r: row, c: sheetRange.e.c },
  }));

  // apply 1st collumn styling
  applyCellStyling({
    ws,
    style: {
      fill: {
        fgColor: { rgb: 'f0f0f0' },
      },
      border: {
        top: REGULAR_BORDER,
        bottom: REGULAR_BORDER,
        left: REGULAR_BORDER,
        right: REGULAR_BORDER,
      },
    },
    range: {
      s: { r: addOnRowCount, c: 0 },
      e: { r: sheetRange.e.r, c: 0 },
    },
  });

  // apply 1st row styling (title section)
  if (titleRow) {
    applyCellStyling({
      ws,
      style: {
        font: {
          bold: true,
        },
        alignment: {
          horizontal: 'center',
        },
      },
      range: {
        s: { r: 0, c: sheetRange.s.c },
        e: { r: 0, c: sheetRange.e.c },
      },
    });
  }

  // apply header styling
  applyCellStyling({
    ws,
    style: {
      font: {
        bold: true,
      },
      fill: {
        fgColor: { rgb: 'bababa' },
      },
      alignment: {
        vertical: 'center',
      },
      border: {
        top: HEADER_BORDER,
        bottom: HEADER_BORDER,
        left: HEADER_BORDER,
        right: HEADER_BORDER,
      },
    },
    range: {
      s: { r: addOnRowCount, c: sheetRange.s.c },
      e: { r: addOnRowCount, c: sheetRange.e.c },
    },
  });

  // set header height
  if (!ws['!rows']) ws['!rows'] = [];
  ws['!rows'][addOnRowCount] = { hpt: 20 };

  // set collumn width
  if (!ws['!cols']) ws['!cols'] = [];
  for (let colIndex = sheetRange.s.c; colIndex <= sheetRange.e.c; ++colIndex) {
    ws['!cols'][colIndex] = { width: customWidth[colIndex] ?? DEFAULT_WIDTH };
  }

  const wb = { Sheets: { data: ws }, SheetNames: ['data'] };
  XLSX.writeFile(wb, fileName + FILE_EXTENSION, { cellStyles: true });
};

type ParseValueFallbackType<TFallback> = TFallback extends (infer U)[]
  ? U
  : TFallback;

type ParseValueReturnType<TValue, TFallback> = TValue extends Nullish
  ? TFallback extends Nullish
    ? string // Default to string if fallback is null or undefined
    : ParseValueFallbackType<TFallback>
  : TValue;

const parseValue = <TValue = string, TFallback = string>(
  value?: TValue,
  fallbacks?: TFallback,
): ParseValueReturnType<TValue, TFallback> =>
  value ??
  (Array.isArray(fallbacks) && fallbacks.length
    ? fallbacks.find(fallback => !!fallback)
    : fallbacks) ??
  '-';

// will get rid of the 0 after the comma
const getRoundedValue = (number: number, places = 1) => {
  const calculationFactor = Math.pow(10, places);
  return Math.round(number * calculationFactor) / calculationFactor;
};

const capitalize = <T extends string>(s: T) => {
  const arrOfStr = s.split(' ');

  return arrOfStr
    .map(s =>
      s.length
        ? ((s[0]?.toUpperCase() + s.slice(1).toLowerCase()) as Capitalize<
            typeof s
          >)
        : s,
    )
    .join(' ');
};

interface GetSelectedOptionProps {
  value: Value;
  options: Option[] | GroupedOption[];
}

const getSelectedOption = ({ value, options }: GetSelectedOptionProps) => {
  const toFind =
    options[0] && 'options' in options[0]
      ? (options as GroupedOption[]).reduce<Option[]>((acc, curr) => {
          return [...acc, ...curr.options];
        }, [])
      : (options as Option[]);

  return (
    toFind?.find(
      option => JSON.stringify(option.value) === JSON.stringify(value),
    ) ?? null
  );
};

const checkIsSameDate = (date1?: Date | null, date2?: Date | null) =>
  !!date1 &&
  !!date2 &&
  date1.getFullYear() === date2.getFullYear() &&
  date1.getMonth() === date2.getMonth() &&
  date1.getDate() === date2.getDate();

const downloadFileFromBlob = (data: Blob, fileName: string) => {
  const fileDownloadUrl = URL.createObjectURL(data);

  const anchorEl = document.createElement('a');
  anchorEl.href = fileDownloadUrl;
  anchorEl.download = fileName;

  document.body.appendChild(anchorEl);
  anchorEl.click();

  document.body.removeChild(anchorEl);
  URL.revokeObjectURL(fileDownloadUrl);
};
const getYoutubeVideoId = (youtubeUrl: string) => {
  const regex =
    /^.*(?:(?:youtu\.be\/|v\/|vi\/|u\/\w\/|embed\/|shorts\/)|(?:(?:watch)?\?v(?:i)?=|&v(?:i)?=))([^#&?]*).*/;

  return regex.exec(youtubeUrl)?.[1];
};

const getYoutubeThumbnail = (youtubeUrl: string) => {
  // usual format: http://img.youtube.com/vi/[video-id]/[thumbnail-number].jpg

  return `http://img.youtube.com/vi/${getYoutubeVideoId(youtubeUrl)}/0.jpg`;
};

export interface Location {
  lat?: number;
  lng?: number;
}

/**
 * haversine distance, ref: https://mapsplatform.google.com/resources/blog/how-calculate-distances-map-maps-javascript-api/
 * @returns distance in KM
 */
const getDistanceFrom2LongLat = (location1: Location, location2: Location) => {
  if (!location1.lat || !location1.lng || !location2.lat || !location2.lng)
    return 0;

  const rLat1 = location1.lat * (Math.PI / 180); // Convert degrees to radians
  const rLat2 = location2.lat * (Math.PI / 180); // Convert degrees to radians
  const diffLat = rLat2 - rLat1; // Radian difference (latitudes)
  const diffLon = (location2.lng - location1.lng) * (Math.PI / 180); // Radian difference (longitudes)

  return (
    2 *
    EARTH_RADIUS *
    Math.asin(
      Math.sqrt(
        Math.sin(diffLat / 2) * Math.sin(diffLat / 2) +
          Math.cos(rLat1) *
            Math.cos(rLat2) *
            Math.sin(diffLon / 2) *
            Math.sin(diffLon / 2),
      ),
    )
  );
};

const getGoogleMapDirectionLink = (origin: Location, destination: Location) => {
  return `https://www.google.com/maps/dir/?api=1&origin=${origin.lat},${origin.lng}&destination=${destination.lat},${destination.lng}`;
};

export type Nullish = null | undefined;
const isNullish = (value: unknown): value is Nullish => {
  return value === null || value === undefined;
};

const isValidDateStr = (strDate: unknown): strDate is Date => {
  return new Date(strDate as string).toDateString() !== 'Invalid Date'; // cast to string to make TS happy
};

const copyToClipboard = (value: string) => {
  copy(value);

  showSuccess('Text copied to clipboard!');
};

export type EmptyObject = Record<PropertyKey, never>;
const isEmptyObject = (obj: Record<string, unknown>): obj is EmptyObject => {
  return Object.keys(obj).length === 0;
};

export type StringOperator = '<' | '<=' | '>' | '>=' | '=';
const compareByStringOperator = (
  a: number,
  b: number,
  operator: StringOperator,
) => {
  switch (operator) {
    case '<':
      return a < b;
    case '<=':
      return a <= b;
    case '>':
      return a > b;
    case '>=':
      return a >= b;
    default:
      return a === b;
  }
};

export {
  copyToClipboard,
  capitalize,
  formatNumber,
  handleNumberCalculation,
  // ts-prune-ignore-next used in test
  getPropByString,
  sortList,
  sortListMultipleField,
  distinctArray,
  getCurrentDateOfMonth,
  getCurrentMonth,
  getCurrentYear,
  getCurrentDate,
  filteredPhoneNumber,
  getWeekRangeList,
  // ts-prune-ignore-next used in test
  getWeekRangeFromDateForCurrentMonth,
  getAgentRankingName,
  getAgentRankingNumber,
  isDateInSetTargetPeriod,
  getMergedArray,
  getAREDetailWithPerformance,
  getAREDetailWithPerformanceV2,
  doNothing,
  getJoinVisitSummaryAttributeListsByActivityType,
  getAchievementRateInPercentage,
  getAreTargetDetailData,
  getPerformanceAreDetailData,
  getPerformanceAreDetailDataV2,
  convertToXlsxFileThenDownload,
  parseValue,
  getRoundedValue,
  getSelectedOption,
  checkIsSameDate,
  downloadFileFromBlob,
  getYoutubeVideoId,
  getYoutubeThumbnail,
  getDistanceFrom2LongLat,
  getGoogleMapDirectionLink,
  isNullish,
  isValidDateStr,
  isEmptyObject,
  compareByStringOperator,
};
